import { ActionTree } from 'vuex'
import api from '../api'
import { ContactsStoredType } from '@orgmap/types/enums'
import { OrgMemberListMutationPayload } from './mutations'
import * as types from './types'
import Vue from 'vue'

export interface OrgMemberListActionPayload {
  comCode: string
  deptCode: string
}

export default <ActionTree<orgmap.state.OrgmapState, RootState>>{
  //
  // ──────────────────────────────────────────── I ──────────
  //   :::::: H R : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────
  //

  async [types.ORG_MEMBER_LIST](action, payload: OrgMemberListActionPayload) {
    const { comCode, deptCode } = payload
    action.commit(types.ORG_MEMBER_SELECT_ALL, false)
    const { items, totalCount } = await api.hr.getEmployeeList({ deptCode, comCode })
    const members = items.map<orgmap.TMemberItem>((x) => {
      return {
        id: x.personCode,
        name: x.displayName,
        nameEn: x.displayNameEng || '',
        nameCh: x.displayNameChi || '',
        deptCode: x.deptCode,
        deptName: x.deptName,
        deptNameEn: x.deptNameEn || '',
        deptNameCh: x.deptNameCh || '',
        photo: x.photoPath,
        titleCode: x.gradeCode,
        title: x.titleName || '',
        titleEn: x.titleNameEn || '',
        titleCh: x.titleNameCh || '',
        positionCode: x.positionCode,
        positionName: x.positionName,
        positionNameEn: x.positionNameEn,
        roleCode: x.roleCode,
        roleName: x.roleName,
        roleNameEn: x.roleNameEn,
        roleNameCh: x.roleNameCh,
        gradeCode: x.gradeCode,
        gradeName: x.gradeName,
        gradeNameEn: x.gradeNameEn,
        gradeNameCh: x.gradeNameCh,
        email: x.email,
        mobile: x.mobileTel || '',
        tel: x.officeTel || '',
        officeFax: x.officeFax || '',
        comCode: x.comCode,
        company: x.companyName || '',
        companyEn: x.companyNameEng || '',
        companyCh: x.companyNameChi || '',
        personCode: x.personCode,
        personId: x.personId,
        selected: false,
        employeeNumber: x.employeeNumber || '',
        jobRoleName : x.jobRoleName,
        jobRole : x.jobRole
      }
    })
    action.commit(types.ORG_MEMBER_LIST, {
      items: members,
      totalCount,
      selectedAll: false,
    } as OrgMemberListMutationPayload)
  },

  async [types.ORG_MEMBER_SEARCH](
    action,
    payload: { option: orgmap.SearchOptions; comCode: string }
  ) {
    const { items, totalCount } = await api.hr.searchOrg(payload.option, payload.comCode)
    const members = items.map<orgmap.TMemberItem>((x) => {
      return {
        id: x.id,
        name: x.displayName,
        nameEn: x.displayNameEng,
        nameCh: x.displayNameChi,
        deptCode: x.deptCode,
        deptName: x.deptName,
        deptNameEn: x.deptNameEn || '',
        deptNameCh: x.deptNameCh || '',
        photo: x.photoPath,
        titleCode: x.titleCode,
        title: x.titleName || '',
        titleEn: x.titleNameEn || '',
        titleCh: x.titleNameCh || '',
        positionCode: x.positionCode,
        positionName: x.positionName,
        positionNameEn: x.positionNameEn,
        roleCode: x.roleCode,
        roleName: x.roleName,
        roleNameEn: x.roleNameEn,
        roleNameCh: x.roleNameCh,
        email: x.email,
        mobile: x.mobileTel || '',
        tel: x.officeTel || '',
        officeFax: x.officeFax || '',
        comCode: x.comCode,
        company: x.companyName || '',
        companyEn: x.companyNameEng || '',
        personCode: x.id,
        personId: x.personId,
        selected: false,
        jobRoleName : x.jobRoleName,
        jobRole : x.jobRole
      }
    })
    action.commit(types.ORG_MEMBER_LIST, {
      items: members,
      totalCount,
      selectedAll: false,
    } as OrgMemberListMutationPayload)
  },

  //
  // ──────────────────────────────────────────────────── I ──────────
  //   :::::: P U B L I C : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────
  //

  async [types.ORG_PUBLIC_FOLDER_LIST](action) {
    return await api.publics.getPublicContactsList().then((res) => res)
  },

  async [types.ORG_PUBLIC_MEMBER_LIST](action, payload: orgmap.DLGroupApiOption) {
    action.commit(types.ORG_MEMBER_SELECT_ALL, false)
    const items = await api.publics.getPublicContactsMembersList(payload)
    const members = items.map<orgmap.TMemberItem>((x: any) => {
      return {
        id: x.id,
        name: x.displayName,
        nameEn: x.displayNameEng,
        nameCh: x.displayNameChi,
        deptCode: x.deptCode,
        deptName: x.deptName,
        deptNameEn: x.deptNameEn || '',
        deptNameCh: x.deptNameCh || '',
        photo: x.photoPath,
        title: x.titleName,
        titleEn: x.titleNameEn || '',
        titleCh: x.titleCh || '',
        email: x.email,
        mobile: x.mobileTel || '',
        tel: x.officeTel || '',
        comCode: x.comCode,
        company: x.companyName || '',
        personCode: x.identity,
        selected: false,
      }
    })

    action.commit(types.ORG_MEMBER_LIST, {
      items: members,
      totalCount: members.length,
      selectedAll: false,
    } as OrgMemberListMutationPayload)
  },

  //
  // ──────────────────────────────────────────────────────── I ──────────
  //   :::::: P E R S O N A L : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────────
  //

  async [types.ORG_PERSONAL_FOLDER_LIST](action) {
    var funcArr = [
      api.personal.getBasicContactFolder(),
      api.personal.getContactTree()      
    ]
    return await Promise.all(funcArr).then((res: Array<orgmap.PersonalFolder[]>) => {
      return res.flat()
    })
  },

  async [types.ORG_PERSONAL_MEMBER_LIST](
    action,
    payload: { data: orgmap.api.DynamicApiOptions; type: ContactsStoredType }
  ) {
    const { data, type } = payload
    action.commit(types.ORG_MEMBER_SELECT_ALL, false)
    const { items, totalCount } = await api.personal.getContactsList(data, type)
    const members = items.map<orgmap.TMemberItem>((x: any) => {
      return {
        id: x.id,
        name: x.displayName,
        nameEn: x.displayNameEng,
        nameCh: x.displayNameCh,
        deptCode: x.department,
        deptName: x.department,
        deptNameEn: x.department,
        deptNameCh: x.deptNameCh,
        photo: x.photoPath,
        title: x.jobTitle,
        titleEn: x.jobTitle,
        titleCh: x.titleCh,
        email: x.email,
        mobile: x.mobileTel || '',
        tel: x.officeTel || '',
        comCode: x.comCode,
        company: x.companyName || '',
        personCode: '',
        selected: false,
        type: x.itemClass || x.type,
      }
    })

    action.commit(types.ORG_MEMBER_LIST, {
      items: members,
      totalCount,
      selectedAll: false,
    } as OrgMemberListMutationPayload)   
  },

  async [types.ORG_PERSONAL_GROUPMEMBER_LIST](
    action,
    payload: { item: orgmap.TMemberItem; type: ContactsStoredType }
  ) {
    const { item, type } = payload
    const { items, totalCount } = await api.group.getGroupMembers(item.id, type)
    const children = items.map<orgmap.TMemberItem>((x: any) => {
      return {
        id: x.id,
        name: x.displayName,
        nameEn: x.displayNameEng,
        nameCh: x.displayNameCh,
        deptCode: x.department,
        deptName: x.department,
        deptNameEn: x.department,
        deptNameCh: x.deptNameCh,
        photo: x.photoPath,
        title: x.jobTitle,
        titleEn: x.jobTitle,
        titleCh: x.titleCh,
        email: x.email,
        mobile: x.mobileTel || '',
        tel: x.officeTel || '',
        comCode: x.comCode,
        company: x.companyName || '',
        personCode: x.targetPersonCode || '',
        selected: false,
        type: x.type,
      }
    })

    const found = action.state.members.items.find((x) => x.id === item.id)
    if (found) {
      found.children = children
    }
  },

  [types.SEARCH_CHANGE](action, options: orgmap.SearchOptions) {
      action.commit(types.SEARCH_CHANGE, options)
  },
  
  //
  // ──────────────────────────────────────────────────── I ──────────
  //   :::::: C O M P A N Y : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────
  //

  [types.ORG_COMPANY_CURRENT](action, company: orgmap.Company) {
    action.commit(types.ORG_COMPANY_CURRENT, company)
    action.commit(types.SEARCH_CHANGE, {
      ...action.state.search,
      totalCount: 0,
      query: null,
    })

    const requestDeptListOptions: orgmap.DepartmentOption = {
      comCode: company.comCode,
      level: 2,
    }
    return action.dispatch(types.HR_TREE_LIST, requestDeptListOptions)
  },

  [types.ORG_COMPANY_LOAD](action) {
    return api.hr
      .getCompanies()
      .then(async (companies: Array<orgmap.Company>) => {
        action.commit(types.ORG_COMPANY_LOAD, companies)
        await action.dispatch(types.ORG_COMPANY_CURRENT, companies[0])
        return companies
      })
  },

  [types.ORG_COMPANY_LOAD_WITHDISABLE](action) {
    return api.hr
      .getCompanies(true)
      .then(async (companies: Array<orgmap.Company>) => {
        action.commit(types.ORG_COMPANY_LOAD_WITHDISABLE, companies)
        await action.dispatch(types.ORG_COMPANY_CURRENT, companies[0])
        return companies
      })
  },

  //
  // ──────────────────────────────────────────────────── I ──────────
  //   :::::: H R : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────
  //

  [types.HR_TREE_LIST](action, payload: orgmap.DepartmentOption): Promise<any> {
    return api.hr
      .getOrgTree(payload)
      .then((res) => res)
      .then((items) => {
        const data = {
          parentDeptCode: payload.parentDeptCode,
          children: items,
        }
        action.commit(types.HR_TREE_LIST, data)
        return items
      })
  },

  [types.HR_TREE_LIST_WITH_DISABLE](action, payload: orgmap.DepartmentOption): Promise<any> {
    return api.hr
      .getOrgTreeWithDisable(payload)
      .then((res) => res)
      .then((items) => {
        const data = {
          parentDeptCode: payload.parentDeptCode,
          children: items,
        }
        action.commit(types.HR_TREE_LIST, data)
        return items
      })
  },

  [types.HR_TREE_CURRENT](action, dept: orgmap.Department) {
    action.commit(types.HR_TREE_CURRENT, dept)
    action.commit(types.SEARCH_CHANGE, { ...action.state.search, query: null })
    action.dispatch(types.HR_USERS_LIST, dept)
  },

  [types.HR_TREE_CURRENT_V2](action, dept: orgmap.Department) {
    action.commit(types.HR_TREE_CURRENT_V2, dept)
    action.commit(types.SEARCH_CHANGE, { ...action.state.search, query: null })
    action.dispatch(types.HR_USERS_LIST_V2, dept)
  },

  [types.HR_TREE_CURRENT_V3](action, dept: orgmap.Department) {
    action.commit(types.HR_TREE_CURRENT, dept)
    action.dispatch(types.HR_USERS_LIST, dept)
  },

  [types.HR_TREE_PATH](action, payload: orgmap.DepartmentOption): Promise<any> {
    return api.hr
      .getDeptPath(payload)
      .then((res) => res.items)
      .then((items) => items[0].path.split('/').slice(1))
      .then((paths) => {
        var currentDepts = action.state.hr.dept.items
        var dept: orgmap.Department
        var updatableDept: orgmap.Department
        paths = paths.reverse()

        const load = function(path: string) {
          if (path) {
            // a. 부서 검색
            if (dept) {
              if (dept.children) {
                dept = dept.children.filter((dept) => dept.id === path)[0]
              }
            } else {
              dept = currentDepts.filter((dept) => dept.id === path)[0]
            }
            // b. children 없을 시 조회
            if (
              dept !== undefined &&
              dept.hasChildren &&
              (!dept.children || dept.children.length === 0)
            ) {
              api.hr
                .getOrgTree({
                  parentDeptCode: path,
                  comCode: payload.comCode,
                  level: 1,
                })
                .then((res) => res)
                .then((items) => {
                  dept.children = items
                  if (!updatableDept) {
                    updatableDept = dept
                  } else {
                    if (updatableDept.deptCode === dept.parentDeptCode) {
                      action.commit(types.HR_TREE_LIST, {
                        parentDeptCode: updatableDept.deptCode,
                        children: updatableDept.children,
                      })
                      updatableDept = dept
                    }
                  }
                })
                .then(() => load(paths.pop()))
            } else {
              load(paths.pop())
            }
          } else {
            // c. commit list
            if (updatableDept) {
              action.commit(types.HR_TREE_LIST, {
                parentDeptCode: updatableDept.deptCode,
                children: updatableDept.children,
              })
            }
            action.dispatch(types.HR_TREE_CURRENT, dept)
          }
        }
        load(paths.pop())
      })
  },

  [types.HR_TREE_PATH_V2](action, payload: orgmap.DepartmentOption): Promise<any> {
    return api.hr
      .getDeptPath(payload)
      .then((res) => res.items)
      .then((items) => items[0].path.split('/').slice(1))
      .then((paths) => {
        var currentDepts = action.state.hr.dept.items
        var dept: orgmap.Department
        var updatableDept: orgmap.Department
        paths = paths.reverse()

        const load = function(path: string) {
          if (path) {
            // a. 부서 검색
            if (dept) {
              if (dept.children) {
                dept = dept.children.filter((dept) => dept.id === path)[0]
              }
            } else {
              dept = currentDepts.filter((dept) => dept.id === path)[0]
            }
            // b. children 없을 시 조회
            if (
              dept !== undefined &&
              dept.hasChildren &&
              (!dept.children || dept.children.length === 0)
            ) {
              api.hr
                .getOrgTree({
                  parentDeptCode: path,
                  comCode: payload.comCode,
                  level: 1,
                })
                .then((res) => res)
                .then((items) => {
                  dept.children = items
                  if (!updatableDept) {
                    updatableDept = dept
                  } else {
                    if (updatableDept.deptCode === dept.parentDeptCode) {
                      action.commit(types.HR_TREE_LIST, {
                        parentDeptCode: updatableDept.deptCode,
                        children: updatableDept.children,
                      })
                      updatableDept = dept
                    }
                  }
                })
                .then(() => load(paths.pop()))
            } else {
              load(paths.pop())
            }
          } else {
            // c. commit list
            if (updatableDept) {
              action.commit(types.HR_TREE_LIST, {
                parentDeptCode: updatableDept.deptCode,
                children: updatableDept.children,
              })
            }
            action.dispatch(types.HR_TREE_CURRENT_V2, dept)
          }
        }
        load(paths.pop())
      })
  },

  [types.HR_TREE_PATH_WITH_DISABLE](action, payload: orgmap.DepartmentOption): Promise<any> {
    return api.hr
      .getDeptPath(payload)
      .then((res) => res.items)
      .then((items) => items[0].path.split('/').slice(1))
      .then((paths) => {
        var currentDepts = action.state.hr.dept.items
        var dept: orgmap.Department
        var updatableDept: orgmap.Department
        paths = paths.reverse()

        const load = function(path: string) {
          if (path) {
            // a. 부서 검색
            if (dept) {
              if (dept.children) {
                dept = dept.children.filter((dept) => dept.id === path)[0]
              }
            } else {
              dept = currentDepts.filter((dept) => dept.id === path)[0]
            }
            // b. children 없을 시 조회
            if (
              dept !== undefined &&
              dept.hasChildren &&
              (!dept.children || dept.children.length === 0)
            ) {
              api.hr
                .getOrgTreeWithDisable({
                  parentDeptCode: path,
                  comCode: payload.comCode,
                  level: 1,
                })
                .then((res) => res)
                .then((items) => {
                  dept.children = items
                  if (!updatableDept) {
                    updatableDept = dept
                  } else {
                    if (updatableDept.deptCode === dept.parentDeptCode) {
                      action.commit(types.HR_TREE_LIST, {
                        parentDeptCode: updatableDept.deptCode,
                        children: updatableDept.children,
                      })
                      updatableDept = dept
                    }
                  }
                })
                .then(() => load(paths.pop()))
            } else {
              load(paths.pop())
            }
          } else {
            // c. commit list
            if (updatableDept) {
              action.commit(types.HR_TREE_LIST, {
                parentDeptCode: updatableDept.deptCode,
                children: updatableDept.children,
              })
            }
            action.dispatch(types.HR_TREE_CURRENT, dept)
          }
        }
        load(paths.pop())
      })
  },

  [types.HR_USERS_LIST](action, payload: orgmap.Department) {
    var dept = payload || action.state.hr.dept.current
    if (dept) {
      return api.hr
        .getEmployeeList(dept)
        .then((res) => {
          action.commit(types.HR_USERS_LIST, res)
        })
        .catch((err: Error) => {
          console.log('HR_USERS_LIST', 'error', err)
        })
    } else {
      action.commit(types.HR_USERS_LIST, {
        items: [],
        moreAvailable: false,
        totalCount: 0,
      } as orgmap.PagedItemList<orgmap.UserItem>)
    }
  },

  [types.HR_USERS_LIST_V2](action, payload: orgmap.Department) {
    var dept = payload || action.state.hr.dept.current
    if (dept) {
      return api.hr
        .getEmployeeList(dept)
        .then((res) => {
          action.commit(types.HR_USERS_LIST_V2, res)
        })
        .catch((err: Error) => {
          console.log('HR_USERS_LIST_V2', 'error', err)
        })
    } else {
      action.commit(types.HR_USERS_LIST_V2, {
        items: [],
        moreAvailable: false,
        totalCount: 0,
      } as orgmap.PagedItemList<orgmap.UserItem>)
    }
  },

  [types.HR_USERS_SEARCH](action, options: orgmap.SearchOptions) {
    //const comCode = action.state.hr.currentCompany?.comCode
    const comCode = action.state.hr.dept.current?.comCode
    if (options.query && comCode) {
      return api.hr.searchOrg(options, comCode).then((res) => {
        var items = res.items.map((x: any) => {
          if (x.type === 'D') {
            return {
              ...x,
            } as orgmap.UserItem
          } else {
            return {
              ...x,
              personCode: x.id,
            } as orgmap.UserItem
          }
        })
        res.items = items
        action.commit(types.SEARCH_CHANGE, { totalCount: res.totalCount })
        action.commit(types.HR_USERS_LIST, res)
        return res
      })
    }
  },

  [types.HR_USER_SELALL](action, selectAll: boolean) {
    action.commit(types.HR_USER_SELALL, selectAll)
  },

  [types.HR_USERS_DEPTPERMSN](action, options: orgmap.SearchOptions) {
    //const comCode = action.state.hr.currentCompany?.comCode
    const comCode = action.state.hr.dept.current?.comCode
    if (options.query && comCode) {
      return api.hr.deptPermsn(options, comCode).then((res) => {
        var items = res.items.map((x: any) => {
          if (x.type === 'D') {
            return {
              ...x,
            } as orgmap.UserItem
          } else {
            return {
              ...x,
              personCode: x.id,
            } as orgmap.UserItem
          }
        })
        res.items = items
        action.commit(types.SEARCH_CHANGE, { totalCount: res.totalCount })
        action.commit(types.HR_USERS_LIST, res)
        return res
      })
    }
  },

  [types.HR_USERS_DEPTPERMSN_WITH_DISABLE](action, options: orgmap.SearchOptions) {
    //const comCode = action.state.hr.currentCompany?.comCode
    const comCode = action.state.hr.dept.current?.comCode
    if (options.query && comCode) {
      return api.hr.deptPermsnWithDisable(options, comCode).then((res) => {
        var items = res.items.map((x: any) => {
          if (x.type === 'D') {
            return {
              ...x,
            } as orgmap.UserItem
          } else {
            return {
              ...x,
              personCode: x.id,
            } as orgmap.UserItem
          }
        })
        res.items = items
        action.commit(types.SEARCH_CHANGE, { totalCount: res.totalCount })
        action.commit(types.HR_USERS_LIST, res)
        return res
      })
    }
  },

}
